import { useMemo, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useFirebaseContext } from '@context/firebaseContext'
import { getRegionZone, getCurrentLocation } from '@helpers/geo-location'

import useLocalStorage from '@hooks/useLocalStorage'
import {
  addItemsToCart,
  clearCart,
  createCart,
  deleteCart,
  editCart,
  editCartItem,
  getCart,
  removeCartItem,
  removeItemsFromCart,
} from '@services/cart'
import { getSavedCouponObj } from '@services/coupon'
import { debounce } from 'lodash'

const CART_UPDATE_EVENT = 'cart-update'

const useCartV2 = id => {
  const queryClient = useQueryClient()
  const { user } = useFirebaseContext()

  const isUpdatingRegion = useRef(false)

  const [cartInfo, setCartInfo] = useLocalStorage('cartInfo', null)
  const cartId = useMemo(() => id || cartInfo?.id, [id, cartInfo])

  // Listen for cart updates from other tabs
  useEffect(() => {
    const handleCartUpdate = event => {
      if (event.detail?.cartId === cartId) {
        queryClient.setQueryData(['cartV2', cartId], event.detail.cart)
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(CART_UPDATE_EVENT, handleCartUpdate)
      return () => window.removeEventListener(CART_UPDATE_EVENT, handleCartUpdate)
    }
    return undefined
  }, [cartId, queryClient])

  const handleRegionUpdate = debounce(async () => {
    const { distribution_index, region, zone, zip: zipCode } = getRegionZone()
    const distributionIndex = Number.isNaN(Number(distribution_index)) ? 10 : Number(distribution_index)

    try {
      const updatedCart = await editCartMutation.mutateAsync({
        distributionIndex: distributionIndex || 10,
        region,
        zone,
        zipCode,
      })
      queryClient.setQueryData(['cartV2', cartId], updatedCart)
    } catch (error) {
      console.error('Error updating cart:', error)
    }
  }, 1000)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['cartV2', cartId],
    queryFn: () => getCart(cartId),
    enabled: !!cartId,
    staleTime: 1000, // 1 second
    onSuccess: response => {
      const { zip } = getRegionZone()
      if (response.zipCode !== zip && !isUpdatingRegion.current && response.type === 'online') {
        isUpdatingRegion.current = true
        handleRegionUpdate()
      }
    },
    // Below are settings recommended by Cursor to improve performance
    // staleTime: 30000, // 30 seconds
    // cacheTime: 5 * 60 * 1000, // 5 minutes
    // refetchOnWindowFocus: false,
    // refetchOnMount: true,
    // refetchInterval: false,
  })

  const calculateCartSize = lineItems =>
    lineItems.reduce((acc, lineItem) => {
      const itemQuantity = lineItem.quantity || 0
      const selectionQuantity =
        lineItem.additionalOptions?.selections
          ?.filter(
            selection =>
              !(
                selection?.productDetails?.category?.includes('foundation') ||
                selection?.productDetails?.category?.includes('adjustable base')
              ),
          )
          ?.reduce((accum, selection) => accum + selection.quantity * itemQuantity, 0) || 0
      const completeYourSleepQuantity =
        lineItem.additionalOptions?.completeYourSleep?.reduce(
          (accum, cysItem) => accum + cysItem.quantity * itemQuantity,
          0,
        ) || 0
      const addonQuantity =
        lineItem.additionalOptions?.addons?.reduce(
          (accum, addonItem) => accum + addonItem.quantity * itemQuantity,
          0,
        ) || 0
      return acc + itemQuantity + selectionQuantity + completeYourSleepQuantity + addonQuantity
    }, 0)

  const broadcastCartUpdate = cart => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new window.CustomEvent(CART_UPDATE_EVENT, {
          detail: { cartId, cart },
        }),
      )
    }
  }

  const handleSuccess = updatedCart => {
    queryClient.setQueryData(['cartV2', updatedCart._id], updatedCart)
    broadcastCartUpdate(updatedCart)
  }

  const createCartMutation = useMutation(
    async ([lineItems, distributionIndex, region, zone, modularCartId, storeCartDetails]) => {
      const { zip: zipCode } = getCurrentLocation()
      const body = storeCartDetails
        ? {
            storeCartId: storeCartDetails.storeCartId,
            email: storeCartDetails.email,
            source: 'desktop-web',
            type: 'storecart',
          }
        : {
            distributionIndex,
            region,
            source: 'desktop-web',
            type: 'online',
            zone,
            zipCode,
            ...(lineItems ? { lineItems } : {}),
            ...(modularCartId ? { modularCartId } : {}),
          }

      if (user?.uid) {
        body.accountId = user.uid
      }

      const savedCoupon = getSavedCouponObj()
      if (savedCoupon) {
        body.cId = savedCoupon?.id || savedCoupon?.code
      }

      const newCart = await createCart(body)
      setCartInfo({ id: newCart._id })
      return newCart
    },
    { onSuccess: handleSuccess },
  )

  const addCartLineItemsMutation = useMutation(
    async lineItems => {
      const { distribution_index, region, zone } = getRegionZone()
      const distributionIndex = Number(distribution_index) || 10
      // If no cart exists, create a new one
      if (!cartId) {
        const newCart = await createCartMutation.mutateAsync([lineItems, distributionIndex, region, zone])
        return newCart
      }

      // Otherwise, add items to existing cart
      const updatedCart = await addItemsToCart(cartId, { lineItems })
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const addCartCouponMutation = useMutation(
    async couponId => {
      if (!cartId) return null
      const updatedCart = await addItemsToCart(cartId, { cId: couponId })
      return updatedCart
    },
    { onSuccess: cartId ? handleSuccess : () => {} },
  )

  const editCartLineItemMutation = useMutation(
    async ({ lineItemId, body }) => {
      const updatedCart = await editCartItem(cartId, lineItemId, body)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const removeCartLineItemMutation = useMutation(
    async lineItemId => {
      const updatedCart = await removeCartItem(cartId, lineItemId)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  // optional itemIds array; if not provided, all items will be removed
  const removeItemsFromCartMutation = useMutation(
    async items => {
      const body = items ? { itemIds: items } : null
      const updatedCart = await removeItemsFromCart(cartId, body)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const clearCartMutation = useMutation(
    async () => {
      const updatedCart = await clearCart(cartId)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const deleteCartMutation = useMutation(
    async () => {
      const result = await deleteCart(cartId)
      setCartInfo(null)
      return result
    },
    { onSuccess: () => queryClient.removeQueries(['cartV2', cartId]) },
  )

  const editCartMutation = useMutation(
    async body => {
      const updatedCart = await editCart(cartId, body)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const reloadStoreCartMutation = useMutation(
    async () => {
      const updatedCart = await getCart(cartId, true)
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const updateModularCartIdMutation = useMutation(
    async modularCartId => {
      if (!cartId) {
        const { distribution_index, region, zone } = getRegionZone()
        const distributionIndex = Number(distribution_index) || 10

        const newCart = await createCartMutation.mutateAsync([null, distributionIndex, region, zone, modularCartId])
        return newCart
      }

      const updatedCart = await editCart(cartId, { modularCartId })
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const importStoreCartMutation = useMutation(
    async ({ email, storeCartId }) => {
      if (!cartId) {
        const { distribution_index, region, zone } = getRegionZone()
        const distributionIndex = Number(distribution_index) || 10

        const newCart = await createCartMutation.mutateAsync([
          null,
          distributionIndex,
          region,
          zone,
          null,
          { email, storeCartId },
        ])
        return newCart
      }

      const updatedCart = await editCart(cartId, { email, storeCartId })
      return updatedCart
    },
    { onSuccess: handleSuccess },
  )

  const hasStoreCart = data?.lineItems?.some(item => item?.isStoreSku) ?? false
  const hasOnlineCart = data?.lineItems?.some(item => !item?.isStoreSku) ?? false

  let storeInfo = null
  if (data?.storeCartDetails) {
    storeInfo = {
      salesPersonEmail: data?.storeCartDetails?.salesAssociateEmail ?? '',
      salesPersonInfo: data?.storeCartDetails?.salesAssociateName ?? '',
      storeAddress: data?.storeCartDetails?.storeAddress ?? {},
      storeCartId: data?.storeCartDetails?.id ?? '',
      storeName: data?.storeCartDetails?.storeName ?? '',
      storePhoneNumber: data?.storeCartDetails?.storePhoneNumber ?? '',
    }
  }

  return {
    cart: data,
    loading: isLoading,
    error: isError,
    fetchCart: refetch,
    addCartCouponMutation,
    addCartLineItemsMutation,
    editCartLineItemMutation,
    removeCartLineItemMutation,
    removeItemsFromCartMutation,
    clearCartMutation,
    deleteCartMutation,
    editCartMutation,
    reloadStoreCartMutation,
    updateModularCartIdMutation,
    importStoreCartMutation,
    size: calculateCartSize(data?.lineItems ?? []),
    hasStoreCart,
    hasOnlineCart,
    storeInfo,
  }
}

export default useCartV2
